import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { CreditCard, CheckCircle, Loader2, Shield, Lock, RefreshCw, AlertCircle, Calendar, Store } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlanPurchaseModal = ({ open, onOpenChange, plan, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [checkoutInfo, setCheckoutInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [enableRecurring, setEnableRecurring] = useState(true); // Default to TRUE
  const [hasDefaultCard, setHasDefaultCard] = useState(false);
  const [defaultCardInfo, setDefaultCardInfo] = useState(null);
  
  // Payment method selection
  const [availableGateways, setAvailableGateways] = useState({ stripe: false, clover: false });
  const [selectedGateway, setSelectedGateway] = useState('stripe');
  const [showCardForm, setShowCardForm] = useState(false);
  
  // Clover card form state
  const [cardData, setCardData] = useState({
    cardNumber: '',
    expMonth: '',
    expYear: '',
    cvv: '',
    cardholderName: ''
  });

  // Discount state
  const [pendingDiscount, setPendingDiscount] = useState(null);

  useEffect(() => {
    if (open && plan) {
      fetchCheckoutInfo();
      checkDefaultPaymentMethod();
      fetchAvailableGateways();
      fetchPendingDiscount();
    }
  }, [open, plan]);

  const fetchAvailableGateways = async () => {
    try {
      const token = localStorage.getItem('token');
      // Check which payment gateways the clinic has enabled
      const response = await axios.get(`${API}/clinic/payment-gateways`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const gateways = response.data || {};
      setAvailableGateways({
        stripe: gateways.stripe?.enabled || false,
        clover: gateways.clover?.enabled || false
      });
      
      // Set default gateway
      if (gateways.stripe?.enabled) {
        setSelectedGateway('stripe');
      } else if (gateways.clover?.enabled) {
        setSelectedGateway('clover');
      }
    } catch (error) {
      console.error('Error fetching payment gateways:', error);
      // Default to stripe if error
      setAvailableGateways({ stripe: true, clover: false });
    }
  };

  const fetchPendingDiscount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscriptions/pending-discount`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.has_discount) {
        setPendingDiscount(response.data);
      }
    } catch (error) {
      console.error('Error fetching pending discount:', error);
    }
  };

  const fetchCheckoutInfo = async () => {
    setLoadingInfo(true);
    try {
      // Fetch clinic's enrollment fee settings
      const response = await axios.get(`${API}/subscriptions/clinic-settings`);
      const { enrollment_fee, enrollment_fee_enabled } = response.data;
      
      // Calculate discount amount if pending
      let discountAmount = 0;
      if (pendingDiscount?.has_discount) {
        if (pendingDiscount.discount_method === 'percent') {
          discountAmount = plan.price * (pendingDiscount.discount_value / 100);
        } else if (pendingDiscount.discount_method === 'fixed') {
          discountAmount = Math.min(pendingDiscount.discount_value, plan.price);
        } else if (pendingDiscount.discount_method === 'free_month') {
          discountAmount = plan.price * pendingDiscount.discount_value;
        }
      }
      
      const baseTotal = plan.price + (enrollment_fee_enabled ? enrollment_fee : 0);
      
      setCheckoutInfo({
        enrollmentFee: enrollment_fee_enabled ? enrollment_fee : 0,
        planPrice: plan.price,
        discountAmount: discountAmount,
        totalFirstPayment: Math.max(0, baseTotal - discountAmount),
        originalTotal: baseTotal,
        isRecurring: true
      });
    } catch (error) {
      console.error('Error fetching checkout info:', error);
      // Fallback to plan price only
      setCheckoutInfo({
        enrollmentFee: 0,
        planPrice: plan.price,
        discountAmount: 0,
        totalFirstPayment: plan.price,
        originalTotal: plan.price,
        isRecurring: true
      });
    } finally {
      setLoadingInfo(false);
    }
  };

  const checkDefaultPaymentMethod = async () => {
    try {
      const response = await axios.get(`${API}/payment-methods/has-default`);
      setHasDefaultCard(response.data.has_default);
      setDefaultCardInfo(response.data.card_info);
    } catch (error) {
      console.error('Error checking default payment method:', error);
      setHasDefaultCard(false);
    }
  };

  const handlePurchase = async () => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const token = localStorage.getItem('token');
      
      if (selectedGateway === 'stripe') {
        // Stripe checkout flow (existing)
        const response = await axios.post(`${API}/subscriptions/create-checkout`, {
          plan_id: plan.id,
          origin_url: originUrl,
          enable_recurring: enableRecurring
        });

        // Check if in TEST mode and warn user
        if (response.data.is_test_mode) {
          console.warn('⚠️ Stripe is in TEST MODE');
          toast.warning(response.data.payment_mode_warning || 'Test mode - no real charges will be made', {
            duration: 5000
          });
        }

        // Redirect to Stripe Checkout
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        } else {
          throw new Error('No checkout URL received');
        }
      } else if (selectedGateway === 'clover') {
        // Clover checkout flow
        if (!showCardForm) {
          // Show card form for Clover
          setShowCardForm(true);
          setLoading(false);
          return;
        }
        
        // Validate card data
        if (!cardData.cardNumber || !cardData.expMonth || !cardData.expYear || !cardData.cvv || !cardData.cardholderName) {
          toast.error(t('payments.fillAllFields') || 'Please fill all card fields');
          setLoading(false);
          return;
        }
        
        // Process Clover payment
        const response = await axios.post(`${API}/clover/process-membership-payment`, {
          plan_id: plan.id,
          enable_recurring: enableRecurring,
          card_number: cardData.cardNumber.replace(/\s/g, ''),
          exp_month: parseInt(cardData.expMonth),
          exp_year: parseInt(cardData.expYear),
          cvv: cardData.cvv,
          cardholder_name: cardData.cardholderName
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.status === 'success') {
          toast.success(t('payments.paymentSuccess') || 'Payment successful!');
          onSuccess && onSuccess(response.data);
          onOpenChange(false);
        } else {
          throw new Error(response.data.message || 'Payment failed');
        }
      }
    } catch (error) {
      console.error('Purchase failed:', error);
      toast.error(error.response?.data?.detail || error.message || t('common.error'));
      setLoading(false);
    }
  };

  if (!plan) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl">{t('membership.startMembership')}</DialogTitle>
          <DialogDescription>
            {t('membership.reviewDetails')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Plan Summary */}
          <Card className="border-2 border-teal-200 bg-gradient-to-br from-teal-50 to-white">
            <CardContent className="pt-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                  <p className="text-sm text-gray-600 mt-1">{plan.description}</p>
                </div>
                {plan.is_most_popular && (
                  <Badge className="bg-orange-500">{t('membership.mostPopular')}</Badge>
                )}
              </div>

              {/* Benefits */}
              {plan.benefits && plan.benefits.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm font-semibold text-gray-700 mb-2">
                    {t('membership.includedBenefits')}:
                  </p>
                  <div className="space-y-2">
                    {plan.benefits.map((benefit, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-teal-600 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Pricing Breakdown */}
          {loadingInfo ? (
            <Card className="bg-gray-50">
              <CardContent className="py-6 text-center">
                <Loader2 className="h-6 w-6 animate-spin mx-auto text-gray-400" />
                <p className="text-sm text-gray-500 mt-2">{t('common.loading')}...</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-gray-50">
              <CardContent className="pt-6">
                <h4 className="font-semibold text-gray-900 mb-4">{t('payments.summary')}</h4>
                <div className="space-y-3">
                  {/* Membership Fee */}
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-teal-600" />
                      <span className="text-gray-700">
                        {t('membership.membership')} ({plan.duration_months} {plan.duration_months === 1 ? t('membership.month') : t('membership.months')})
                      </span>
                    </div>
                    <span className="font-semibold">${checkoutInfo?.planPrice?.toFixed(2)}</span>
                  </div>
                  
                  {/* Enrollment Fee (if applicable) */}
                  {checkoutInfo?.enrollmentFee > 0 && (
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-700">{t('renewal.enrollmentFee')}</span>
                        <Badge variant="outline" className="text-xs">{t('membership.oneTimeOnly')}</Badge>
                      </div>
                      <span className="font-semibold">${checkoutInfo.enrollmentFee.toFixed(2)}</span>
                    </div>
                  )}
                  
                  {/* Discount Applied */}
                  {pendingDiscount?.has_discount && checkoutInfo?.discountAmount > 0 && (
                    <div className="flex justify-between items-center bg-green-50 p-2 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2">
                        <span className="text-green-700 font-medium">
                          {pendingDiscount.discount_type === 'referral' 
                            ? `🎉 Referral Discount (${pendingDiscount.referrer_name})`
                            : `🏷️ Promo: ${pendingDiscount.name || pendingDiscount.code}`
                          }
                        </span>
                        <Badge className="bg-green-600 text-white text-xs">
                          {pendingDiscount.discount_method === 'percent' 
                            ? `${pendingDiscount.discount_value}% OFF`
                            : pendingDiscount.discount_method === 'fixed'
                            ? `$${pendingDiscount.discount_value} OFF`
                            : `${pendingDiscount.discount_value} Month(s) FREE`
                          }
                        </Badge>
                      </div>
                      <span className="font-semibold text-green-700">
                        -${checkoutInfo.discountAmount.toFixed(2)}
                      </span>
                    </div>
                  )}
                  
                  {/* Divider */}
                  <div className="border-t pt-3 mt-3">
                    {/* Show original price with strikethrough if discounted */}
                    {checkoutInfo?.discountAmount > 0 && (
                      <div className="flex justify-between items-center text-sm text-gray-500 mb-1">
                        <span>Original Total</span>
                        <span className="line-through">${checkoutInfo?.originalTotal?.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900">{t('payments.todaysTotal')}</span>
                      <span className="text-xl font-bold text-teal-600">
                        ${checkoutInfo?.totalFirstPayment?.toFixed(2)}
                      </span>
                    </div>
                    {checkoutInfo?.discountAmount > 0 && (
                      <p className="text-xs text-green-600 text-right mt-1">
                        You save ${checkoutInfo.discountAmount.toFixed(2)}!
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payment Method Selection - Only show if multiple gateways available */}
          {(availableGateways.stripe && availableGateways.clover) && !showCardForm && (
            <Card>
              <CardContent className="pt-4 pb-4">
                <h4 className="font-semibold text-gray-900 mb-3">{t('payments.selectMethod') || 'Select Payment Method'}</h4>
                <RadioGroup value={selectedGateway} onValueChange={setSelectedGateway} className="space-y-3">
                  {availableGateways.stripe && (
                    <div className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${selectedGateway === 'stripe' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <RadioGroupItem value="stripe" id="stripe" />
                      <Label htmlFor="stripe" className="flex items-center gap-2 cursor-pointer flex-1">
                        <div className="w-8 h-8 bg-purple-100 rounded flex items-center justify-center">
                          <CreditCard className="h-4 w-4 text-purple-600" />
                        </div>
                        <div>
                          <span className="font-medium">Stripe</span>
                          <p className="text-xs text-gray-500">Visa, Mastercard, Amex</p>
                        </div>
                      </Label>
                    </div>
                  )}
                  {availableGateways.clover && (
                    <div className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${selectedGateway === 'clover' ? 'border-teal-500 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                      <RadioGroupItem value="clover" id="clover" />
                      <Label htmlFor="clover" className="flex items-center gap-2 cursor-pointer flex-1">
                        <div className="w-8 h-8 bg-green-100 rounded flex items-center justify-center">
                          <Store className="h-4 w-4 text-green-600" />
                        </div>
                        <div>
                          <span className="font-medium">Clover</span>
                          <p className="text-xs text-gray-500">{t('payments.cloverDescription') || 'Pay via Clover'}</p>
                        </div>
                      </Label>
                    </div>
                  )}
                </RadioGroup>
              </CardContent>
            </Card>
          )}

          {/* Clover Card Form */}
          {selectedGateway === 'clover' && showCardForm && (
            <Card className="border-2 border-green-200">
              <CardContent className="pt-4 pb-4 space-y-4">
                <div className="flex items-center gap-2 mb-2">
                  <Store className="h-5 w-5 text-green-600" />
                  <h4 className="font-semibold text-gray-900">{t('payments.enterCardDetails') || 'Enter Card Details'}</h4>
                </div>
                
                <div className="space-y-2">
                  <Label>{t('payments.cardholderName') || 'Cardholder Name'}</Label>
                  <Input
                    placeholder="John Doe"
                    value={cardData.cardholderName}
                    onChange={(e) => setCardData(prev => ({ ...prev, cardholderName: e.target.value }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>{t('payments.cardNumber') || 'Card Number'}</Label>
                  <Input
                    placeholder="4111 1111 1111 1111"
                    value={cardData.cardNumber}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '').replace(/(\d{4})/g, '$1 ').trim();
                      setCardData(prev => ({ ...prev, cardNumber: value }));
                    }}
                    maxLength={19}
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>{t('payments.expMonth') || 'Month'}</Label>
                    <Input
                      placeholder="MM"
                      value={cardData.expMonth}
                      onChange={(e) => setCardData(prev => ({ ...prev, expMonth: e.target.value.replace(/\D/g, '').slice(0, 2) }))}
                      maxLength={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>{t('payments.expYear') || 'Year'}</Label>
                    <Input
                      placeholder="YY"
                      value={cardData.expYear}
                      onChange={(e) => setCardData(prev => ({ ...prev, expYear: e.target.value.replace(/\D/g, '').slice(0, 2) }))}
                      maxLength={2}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>CVV</Label>
                    <Input
                      type="password"
                      placeholder="•••"
                      value={cardData.cvv}
                      onChange={(e) => setCardData(prev => ({ ...prev, cvv: e.target.value.replace(/\D/g, '').slice(0, 4) }))}
                      maxLength={4}
                    />
                  </div>
                </div>
                
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowCardForm(false)}
                  className="mt-2"
                >
                  ← {t('common.back') || 'Back'}
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Recurring Payment Option - DEFAULT CHECKED */}
          <Card className={`border-2 transition-colors ${enableRecurring ? 'border-teal-300 bg-teal-50' : 'border-gray-200 bg-white'}`}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start gap-4">
                <Checkbox 
                  id="enableRecurring" 
                  checked={enableRecurring}
                  onCheckedChange={setEnableRecurring}
                  className="mt-1"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="enableRecurring" className="font-semibold text-gray-900 cursor-pointer">
                      <RefreshCw className="h-4 w-4 inline mr-2 text-teal-600" />
                      {t('recurringPayment.enableRecurring')}
                    </Label>
                    <Badge className="bg-green-500 text-xs">{t('recurringPayment.recommended')}</Badge>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    {enableRecurring 
                      ? t('recurringPayment.recurringDescription')
                      : t('recurringPayment.oneTimeDescription')
                    }
                  </p>
                  
                  {/* Show saved card info if available and recurring is enabled */}
                  {enableRecurring && hasDefaultCard && defaultCardInfo && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-teal-700 bg-white/50 p-2 rounded">
                      <CreditCard className="h-4 w-4" />
                      <span>
                        {t('paymentMethods.default')}: {defaultCardInfo.brand} •••• {defaultCardInfo.last4}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Info about selected option */}
          {!enableRecurring && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                {t('recurringPayment.oneTimeDescription')}
              </p>
            </div>
          )}

          {/* Secure Payment Notice */}
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start gap-3">
                <Shield className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-green-800">
                  <p className="font-semibold mb-1">{t('payments.securePaymentTitle')}</p>
                  <p>{t('renewal.securePayment')}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Methods Accepted */}
          <div className="flex items-center justify-center gap-4 py-2">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <CreditCard className="h-4 w-4" />
              <span>{t('payments.weAccept')}:</span>
            </div>
            <div className="flex gap-2">
              <div className="w-10 h-6 bg-gradient-to-br from-blue-600 to-blue-800 rounded flex items-center justify-center text-white text-xs font-bold">
                VISA
              </div>
              <div className="w-10 h-6 bg-gradient-to-br from-red-500 to-orange-500 rounded flex items-center justify-center text-white text-xs font-bold">
                MC
              </div>
              <div className="w-10 h-6 bg-gradient-to-br from-blue-400 to-blue-600 rounded flex items-center justify-center text-white text-xs font-bold">
                AMEX
              </div>
            </div>
          </div>

          {/* Purchase Button */}
          <div className="flex gap-3 pt-4">
            <Button
              onClick={() => onOpenChange(false)}
              variant="outline"
              className="flex-1"
              disabled={loading}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handlePurchase}
              className="flex-1 bg-gradient-to-r from-teal-600 to-blue-600 hover:from-teal-700 hover:to-blue-700"
              disabled={loading || loadingInfo}
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  {t('renewal.redirecting')}
                </>
              ) : (
                <>
                  <Lock className="h-4 w-4 mr-2" />
                  {enableRecurring ? t('membership.subscribe') : t('membership.payOnce')} - ${checkoutInfo?.totalFirstPayment?.toFixed(2) || plan.price?.toFixed(2)}
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlanPurchaseModal;
